import { Feature, FeatureCollection, Geometry, Point, Polygon } from "geojson";
import { Inference, InferenceCollection, InferenceProperties } from "../types/inferences";
import { theme } from "@chakra-ui/react";
import centroid from "@turf/centroid";
import bbox from "@turf/bbox";
import { SpeciesCount } from "./inferenceStats";

export function formatDate(date: Date) {
  return date.toLocaleString("en-US", { month: "2-digit", day: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false, timeZone: "America/Los_Angeles", timeZoneName: "shortOffset" }).replaceAll("/", "-");
}

export function getLikelySpecies(inference: Inference) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { pixel_coords, distance_to_coastline, distance_to_continental_coastline, nwd, ...detectionClasses } = inference.properties.inference;
  const sorted = Object.entries(detectionClasses).sort((a, b) => (b[1] as number) - (a[1] as number));

  return {
    species: sorted[0][0],
    confidence: (sorted[0][1] as number).toFixed(2)
  };
}

export function getSortedSpecies(species: SpeciesCount) {
  return Object.entries(species)
    .filter((specie) => whaleSpecies.includes(specie[0].split("-")[0]))
    .sort((a, b) => Number(b[1]) - Number(a[1]));
}

export function convertPolygonToPointAndBbox(collection: FeatureCollection<Geometry, InferenceProperties>): InferenceCollection {
  const features = collection.features.map((feature): Feature<Point | Polygon, InferenceProperties> => {
    if (feature.geometry.type === "Point") {
      return feature as Feature<Point, InferenceProperties>;
    }

    // For Polygon features (composite mode), keep the Polygon geometry
    // and use bounding_box from properties if available
    const isComposite = feature.properties.composite_id !== undefined;
    const featureBbox = feature.properties.bounding_box
      ? feature.properties.bounding_box as [number, number, number, number]
      : bbox(feature) as [number, number, number, number];

    if (isComposite && feature.geometry.type === "Polygon") {
      // Keep Polygon geometry for composites
      return {
        ...feature,
        geometry: feature.geometry as Polygon,
        bbox: featureBbox
      } as Feature<Polygon, InferenceProperties>;
    }

    // Non-composite: convert to Point with bbox
    return {
      ...centroid(feature),
      properties: feature.properties,
      bbox: featureBbox
    } as Feature<Point, InferenceProperties>;
  });

  return {
    ...collection,
    features
  };
}

export const INFERENCES_CLASSES = {
  "humpback-whale": {
    label: "Humpback",
    color: theme.colors.blue["500"],
    isWhale: true,
  },
  "gray-whale": {
    label: "Gray",
    color: theme.colors.yellow["500"],
    isWhale: true,
  },
  "vessel": {
    label: "Vessel",
    color: theme.colors.red["500"],
    isWhale: false,
  },
  "infrastructure": {
    label: "Infrastructure",
    color: theme.colors.green["500"],
    isWhale: false,
  },
  "rock": {
    label: "Rock",
    color: theme.colors.teal["500"],
    isWhale: false,
  },
  "aircraft": {
    label: "Aircraft",
    color: theme.colors.orange["500"],
    isWhale: false,
  },
  "background": {
    label: "Background",
    color: theme.colors.purple["500"],
    isWhale: false,
  },
};

export const whaleSpecies = Object.values(INFERENCES_CLASSES)
  .filter((i) => i.isWhale)
  .map((c) => c.label.toLowerCase());

export function getSpeciesColor(species: string): string {
  const speciesKey = species.toLowerCase();
  return speciesKey in INFERENCES_CLASSES
    ? INFERENCES_CLASSES[speciesKey as keyof typeof INFERENCES_CLASSES].color
    : "white"; // Fallback color
}

export function getSpeciesLabel(species: string): string {
  const speciesKey = species.toLowerCase();
  return speciesKey in INFERENCES_CLASSES
    ? INFERENCES_CLASSES[speciesKey as keyof typeof INFERENCES_CLASSES].label
    : speciesKey; // Fallback label
}
